import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Fade,
  Zoom,
  Paper,
  CircularProgress,
  keyframes
} from '@mui/material';
import {
  Close,
  EmojiEvents,
  Star,
  Celebration,
  TrendingUp,
  FavoriteRounded
} from '@mui/icons-material';

// XP counting animation keyframe
const xpCountAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
`;

// Sparkle animation keyframe
const sparkleAnimation = keyframes`
  0% { transform: rotate(0deg) scale(0); opacity: 0; }
  50% { transform: rotate(180deg) scale(1); opacity: 1; }
  100% { transform: rotate(360deg) scale(0); opacity: 0; }
`;

// Glow animation keyframe
const glowAnimation = keyframes`
  0% { box-shadow: 0 0 20px rgba(76, 175, 80, 0.3); }
  50% { box-shadow: 0 0 40px rgba(76, 175, 80, 0.6); }
  100% { box-shadow: 0 0 20px rgba(76, 175, 80, 0.3); }
`;

// 50 encouraging wellness quotes
const wellnessQuotes = [
  "Every step forward is a victory worth celebrating! 🌟",
  "Your commitment to growth inspires greatness within you.",
  "Progress, not perfection, is the path to lasting change.",
  "You're building a stronger, healthier version of yourself.",
  "Small daily improvements lead to stunning long-term results.",
  "Your mental wellness journey is a gift to your future self.",
  "Consistency is your superpower - keep shining! ✨",
  "You're proving that self-care isn't selfish, it's essential.",
  "Every moment of mindfulness is an investment in your peace.",
  "Your dedication to healing is truly admirable.",
  "Growth happens one brave step at a time.",
  "You're writing a beautiful story of resilience and hope.",
  "Your commitment to yourself is the foundation of all success.",
  "Each challenge completed is proof of your inner strength.",
  "You're not just surviving, you're thriving! 🌱",
  "Your wellness journey is uniquely yours - embrace it.",
  "Progress is progress, no matter how small it seems.",
  "You're creating positive ripples in your life and beyond.",
  "Your mental health matters, and so do you.",
  "Every day you choose growth, you choose possibility.",
  "You're building resilience one mindful moment at a time.",
  "Your future self will thank you for today's efforts.",
  "Healing isn't linear, but your commitment is unwavering.",
  "You're proof that change is possible at any moment.",
  "Your journey of self-discovery is beautifully unfolding.",
  "Each completed goal brings you closer to your best life.",
  "You're cultivating inner peace in a chaotic world.",
  "Your dedication to wellness radiates positive energy.",
  "Growth requires courage, and you have it in abundance.",
  "You're creating lasting change from the inside out.",
  "Your mindfulness practice is a beacon of hope.",
  "Every challenge conquered makes you mentally stronger.",
  "You're proof that self-compassion leads to transformation.",
  "Your wellness journey is an act of profound self-love.",
  "Each step forward honors your commitment to growth.",
  "You're building a foundation of lasting mental strength.",
  "Your progress today plants seeds for tomorrow's joy.",
  "You're becoming the person you've always been meant to be.",
  "Your mental wellness journey inspires others to begin theirs.",
  "Each milestone reached is a testament to your resilience.",
  "You're creating space for peace in your busy life.",
  "Your commitment to healing lights the way for others.",
  "Every moment of self-care is an act of rebellion against stress.",
  "You're proving that investing in yourself always pays dividends.",
  "Your journey shows that healing and hope go hand in hand.",
  "Each challenge completed is a victory for your mental health.",
  "You're building emotional resilience that will last a lifetime.",
  "Your dedication to wellness is changing your life's trajectory.",
  "You're not just improving yourself, you're inspiring transformation.",
  "Your mental wellness journey is a masterpiece in progress. 🎨"
];

const ChallengeCompletionModal = ({ 
  open, 
  onClose, 
  challengeTitle, 
  xpEarned, 
  challengeCategory 
}) => {
  const [animatedXP, setAnimatedXP] = useState(0);
  const [showSparkles, setShowSparkles] = useState(false);
  const [currentQuote, setCurrentQuote] = useState('');

  // Select random quote when modal opens
  useEffect(() => {
    if (open) {
      const randomQuote = wellnessQuotes[Math.floor(Math.random() * wellnessQuotes.length)];
      setCurrentQuote(randomQuote);
      setAnimatedXP(0);
      setShowSparkles(false);
      
      // Animate XP counting
      setTimeout(() => {
        let current = 0;
        const increment = Math.ceil(xpEarned / 20);
        const timer = setInterval(() => {
          current += increment;
          if (current >= xpEarned) {
            current = xpEarned;
            clearInterval(timer);
            setShowSparkles(true);
          }
          setAnimatedXP(current);
        }, 50);
      }, 500);
    }
  }, [open, xpEarned]);

  // Get category icon and color
  const getCategoryDisplay = (category) => {
    const categoryMap = {
      'journal': { icon: <FavoriteRounded />, color: '#E91E63', bgColor: '#FCE4EC' },
      'meditation': { icon: <Star />, color: '#9C27B0', bgColor: '#F3E5F5' },
      'conversation': { icon: <TrendingUp />, color: '#2196F3', bgColor: '#E3F2FD' },
      'mindfulness': { icon: <Star />, color: '#FF9800', bgColor: '#FFF3E0' },
      'wellness': { icon: <FavoriteRounded />, color: '#4CAF50', bgColor: '#E8F5E8' }
    };
    
    return categoryMap[category] || { icon: <EmojiEvents />, color: '#4CAF50', bgColor: '#E8F5E8' };
  };

  const categoryDisplay = getCategoryDisplay(challengeCategory);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          overflow: 'visible',
          position: 'relative'
        }
      }}
      TransitionComponent={Zoom}
      TransitionProps={{ timeout: 500 }}
    >
      {/* Sparkle effects */}
      {showSparkles && (
        <>
          {[...Array(8)].map((_, i) => (
            <Box
              key={i}
              sx={{
                position: 'absolute',
                top: `${20 + Math.random() * 60}%`,
                left: `${10 + Math.random() * 80}%`,
                width: 8,
                height: 8,
                background: '#FFD700',
                borderRadius: '50%',
                animation: `${sparkleAnimation} 2s ease-in-out ${i * 0.2}s`,
                zIndex: 1000
              }}
            />
          ))}
        </>
      )}

      <DialogContent sx={{ p: 0, position: 'relative' }}>
        {/* Close button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: 'white',
            zIndex: 10,
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.1)'
            }
          }}
        >
          <Close />
        </IconButton>

        {/* Main content */}
        <Box sx={{ 
          p: 4, 
          textAlign: 'center',
          color: 'white',
          minHeight: 400,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}>
          {/* Trophy icon with category badge */}
          <Box sx={{ position: 'relative', mb: 3 }}>
            <Box
              sx={{
                display: 'inline-flex',
                p: 3,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                animation: `${glowAnimation} 2s ease-in-out infinite`,
                border: '2px solid rgba(255,255,255,0.3)'
              }}
            >
              <EmojiEvents sx={{ fontSize: 60, color: '#FFD700' }} />
            </Box>
            
            {/* Category badge */}
            <Paper
              sx={{
                position: 'absolute',
                bottom: -10,
                right: -10,
                p: 1,
                borderRadius: 2,
                background: categoryDisplay.bgColor,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                border: `2px solid ${categoryDisplay.color}`
              }}
            >
              {React.cloneElement(categoryDisplay.icon, { 
                sx: { fontSize: 16, color: categoryDisplay.color } 
              })}
              <Typography 
                variant="caption" 
                sx={{ 
                  color: categoryDisplay.color, 
                  fontWeight: 'bold',
                  fontSize: '0.7rem'
                }}
              >
                {challengeCategory?.toUpperCase()}
              </Typography>
            </Paper>
          </Box>

          {/* Congratulations text */}
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 'bold', 
              mb: 2,
              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}
          >
            Challenge Complete! 🎉
          </Typography>

          {/* Challenge title */}
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 3, 
              opacity: 0.9,
              fontWeight: 500,
              textShadow: '0 1px 2px rgba(0,0,0,0.2)'
            }}
          >
            {challengeTitle}
          </Typography>

          {/* XP earned */}
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 'bold',
                color: '#FFD700',
                textShadow: '0 2px 8px rgba(255,215,0,0.4)',
                animation: showSparkles ? `${xpCountAnimation} 0.5s ease-in-out` : 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1
              }}
            >
              +{animatedXP}
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  background: 'rgba(255,215,0,0.2)',
                  px: 2,
                  py: 0.5,
                  borderRadius: 3,
                  border: '2px solid #FFD700'
                }}
              >
                <Star sx={{ fontSize: 24, mr: 0.5 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  XP
                </Typography>
              </Box>
            </Typography>
          </Box>

          {/* Motivational quote */}
          <Paper
            sx={{
              p: 3,
              mb: 4,
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              border: '1px solid rgba(255,255,255,0.2)'
            }}
          >
            <Typography 
              variant="body1" 
              sx={{ 
                fontStyle: 'italic',
                lineHeight: 1.6,
                fontSize: '1.1rem',
                textShadow: '0 1px 2px rgba(0,0,0,0.2)'
              }}
            >
              "{currentQuote}"
            </Typography>
          </Paper>

          {/* Continue button */}
          <Button
            onClick={onClose}
            variant="contained"
            size="large"
            sx={{
              py: 1.5,
              px: 4,
              borderRadius: 3,
              background: 'linear-gradient(45deg, #4CAF50 30%, #81C784 90%)',
              boxShadow: '0 4px 20px rgba(76, 175, 80, 0.3)',
              fontWeight: 'bold',
              fontSize: '1.1rem',
              textTransform: 'none',
              '&:hover': {
                background: 'linear-gradient(45deg, #43A047 30%, #66BB6A 90%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 25px rgba(76, 175, 80, 0.4)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Continue Your Journey
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ChallengeCompletionModal;
